'use client'

import { cn } from '@/lib/utils'
import Image from 'next/image'
import { useState, memo } from 'react'

type MarketImageProps = {
	src?: string
	alt: string
	title: string
	size?: 'sm' | 'md' | 'lg'
	className?: string
}

const SIZE_CLASSES = {
	sm: 'w-8 h-8 text-xs',
	md: 'w-10 h-10 text-sm',
	lg: 'w-12 h-12 text-base',
}

const IMAGE_SIZES = {
	sm: '32px',
	md: '40px',
	lg: '48px',
}

function getInitial(title: string): string {
	return title.charAt(0).toUpperCase()
}

function getPlaceholderColor(title: string): string {
	const colors = [
		'bg-blue-500/20 text-blue-400',
		'bg-purple-500/20 text-purple-400',
		'bg-emerald-500/20 text-emerald-400',
		'bg-amber-500/20 text-amber-400',
		'bg-rose-500/20 text-rose-400',
		'bg-cyan-500/20 text-cyan-400',
		'bg-indigo-500/20 text-indigo-400',
		'bg-orange-500/20 text-orange-400',
	]
	const index = title.charCodeAt(0) % colors.length
	return colors[index]
}

function MarketImageComponent({ src, alt, title, size = 'md', className }: MarketImageProps) {
	const [hasError, setHasError] = useState(false)

	const showPlaceholder = !src || hasError

	if (showPlaceholder) {
		return (
			<div
				className={cn(
					'relative flex flex-shrink-0 items-center justify-center rounded-lg font-semibold',
					SIZE_CLASSES[size],
					getPlaceholderColor(title),
					className
				)}
			>
				{getInitial(title)}
			</div>
		)
	}

	return (
		<div
			className={cn(
				'relative flex-shrink-0 overflow-hidden rounded-lg',
				SIZE_CLASSES[size],
				className
			)}
		>
			<Image
				src={src}
				alt={alt}
				fill
				className="object-cover"
				sizes={IMAGE_SIZES[size]}
				onError={() => setHasError(true)}
			/>
		</div>
	)
}

export const MarketImage = memo(MarketImageComponent)
